import { message } from 'antd';
import { put, takeLatest, select } from 'redux-saga/effects';
import { ProjectPhase } from 'api/project';
import { PositionData } from 'components/organisms/templates/projects/commons/rearrange/types';
import { StoreActionProps } from 'data/core';
import { logger } from 'services';
import { getAutoCorrections } from 'utils/date-shift';
import {
  getPhaseList,
  getTaskList,
  getTemplatePhaseUpdates,
  getTemplateTaskUpdates,
} from 'utils/template-date-shift';
import { updateProjectTemplateParentOfTask } from '../..';
import { rearrangeTasksInProjectTemplate } from '../../actions';
import {
  selectAllTemplateProjectPhase,
  selectAllTemplateTask,
  selectTemplateTask,
} from '../../selectors';
import {
  makeDateShiftsForProjectPhases,
  makeDateShiftsForTasks,
} from '../../slices';
import { ProjectTemplateProjectPhase, ProjectTemplateTask } from '../../types';

function* rearrangeTaskInTemplateSaga(
  action: StoreActionProps<PositionData[]>,
) {
  try {
    const { payload: actions } = action;

    for (let i = 0; i < actions.length; i += 1) {
      const { taskId: id, parent, insertAt } = actions[i];
      const task: ProjectTemplateTask = yield select(selectTemplateTask(id));

      yield put(
        updateProjectTemplateParentOfTask({
          task,
          ...parent,
          insertAt,
        }),
      );
    }
  } catch (error) {
    logger.error('rearrangeTask failed', { error });

    message.error('There was an error in rearranging the task');
  }
}

function* dateShiftOnTasksRearrangeSaga(
  action: StoreActionProps<PositionData[]>,
) {
  try {
    const { payload: actions } = action;
    let phases: { [key: string]: ProjectTemplateProjectPhase } = yield select(
      selectAllTemplateProjectPhase(),
    );
    let tasks: { [key: string]: ProjectTemplateTask } = yield select(
      selectAllTemplateTask(),
    );

    for (let i = 0; i < actions.length; i += 1) {
      const { taskId, parent } = actions[i];

      /**
       * Update parent and child references
       */
      const task = tasks[taskId];
      const {
        projectPhase: newParentPhase = {} as ProjectTemplateProjectPhase,
        parentTask: newParentTask = {} as ProjectTemplateTask,
      } = {
        ...parent,
      };

      /**
       * Remove task reference from current parent
       */
      if (task?.parentTask?.taskId) {
        const currentParentTask = tasks[task.parentTask.taskId];

        tasks = {
          ...tasks,
          [task.parentTask.taskId]: {
            ...currentParentTask,
            subTasks: currentParentTask.subTasks.filter(
              (subTaskRef: ProjectTemplateTask | number) =>
                subTaskRef !== taskId,
            ),
          },
        };
      } else if (task?.projectPhase?.projectPhaseId) {
        const currentProjectPhase = phases[task.projectPhase.projectPhaseId];

        phases = {
          ...phases,
          [task.projectPhase.projectPhaseId]: {
            ...currentProjectPhase,
            tasks: currentProjectPhase.tasks.filter(
              (taskRef: ProjectTemplateTask | number) => taskRef !== taskId,
            ),
          },
        };
      }
      /**
       * Add task reference in new parent and update parent reference in task
       */
      if (newParentPhase?.projectPhaseId) {
        const _phase = phases[newParentPhase.projectPhaseId];

        phases = {
          ...phases,
          [newParentPhase.projectPhaseId]: {
            ..._phase,
            tasks: [taskId, ..._phase.tasks],
          },
        };
        tasks = {
          ...tasks,
          [taskId]: {
            ...task,
            projectPhase: _phase as unknown as ProjectPhase,
            parentTask: undefined,
          },
        };
      } else if (newParentTask?.taskId) {
        const _task = tasks[newParentTask.taskId];

        tasks = {
          ...tasks,
          [newParentTask.taskId]: {
            ..._task,
            subTasks: [taskId, ..._task.subTasks],
          },
          [taskId]: {
            ...task,
            parentTask: _task,
            projectPhase: undefined,
          },
        };
      }
    }
    const taskList = getTaskList(Object.values(tasks));
    const [phaseUpdates, taskUpdates] = getAutoCorrections(
      getPhaseList(Object.values(phases)),
      taskList,
      { allowWeekend: true },
    );

    yield put(
      makeDateShiftsForProjectPhases(getTemplatePhaseUpdates(phaseUpdates)),
    );
    yield put(
      makeDateShiftsForTasks(getTemplateTaskUpdates(taskUpdates, taskList)),
    );
  } catch (error) {
    logger.error('Date shifting during rearrangeTask failed', { error });

    message.error('There was an error in rearranging the task');
  }
}

export function* watchRearrangeTaskInProjectTemplateSaga(): Generator {
  yield takeLatest(
    rearrangeTasksInProjectTemplate.type,
    rearrangeTaskInTemplateSaga,
  );

  yield takeLatest(
    rearrangeTasksInProjectTemplate.type,
    dateShiftOnTasksRearrangeSaga,
  );
}
